import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import ShareLinks from "../components/ShareLinks";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Requisitions() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status_filter") || "";
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (status) q.status_filter = status;
    api
      .get("/requisitions", { params: q })
      .then((r) => setReqs(r.data))
      .finally(() => setLoading(false));
  }, [status]);

  const tabs = [
    ["", "All"],
    ["pending_review", "Pending"],
    ["open", "Open"],
    ["rejected", "Rejected"],
    ["closed", "Closed"],
  ];

  const exportCsv = () => {
    const token = localStorage.getItem("hr_token");
    const url = new URL(`${API_BASE}/export/requisitions.csv`);
    if (status) url.searchParams.set("status_filter", status);
    fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `requisitions_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="requisitions-page">
      <div className="flex items-end justify-between border-b border-rule pb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Workflow · Requisitions</div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Requisitions.</h1>
        </div>
        <div className="flex gap-2 items-center">
          <button
            data-testid="share-link-btn"
            onClick={() => setShareOpen(true)}
            className="border border-ink hover:bg-paper px-3 py-2 text-xs font-mono uppercase tracking-wider"
          >
            Share link
          </button>
          {user?.role === "hr" && (
            <button
              data-testid="export-reqs-csv"
              onClick={exportCsv}
              className="border border-ink hover:bg-paper px-3 py-2 text-xs font-mono uppercase tracking-wider"
            >
              Export CSV
            </button>
          )}
          <Link
            to="/requisitions/new"
            data-testid="new-requisition-btn"
            className="bg-klein hover:bg-klein-hover text-white px-5 py-3 text-sm font-medium transition-colors"
          >
            + Raise requisition
          </Link>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-rule">
        {tabs.map(([k, l]) => (
          <button
            key={k || "all"}
            data-testid={`req-tab-${k || "all"}`}
            onClick={() => {
              if (k) setParams({ status_filter: k });
              else setParams({});
            }}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              status === k
                ? "border-klein text-ink"
                : "border-transparent text-quiet hover:text-ink"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-rule">
        {loading && <div className="px-5 py-12 text-center text-quiet text-sm">Loading…</div>}
        {!loading && reqs.length === 0 && (
          <div className="px-5 py-16 text-center">
            <div className="font-serif text-2xl text-ink">No requisitions in this view</div>
            <div className="text-sm text-quiet mt-2">
              {user?.role === "manager"
                ? "Click 'Raise requisition' to start your first hiring request."
                : "When managers raise requisitions, they'll appear here for your review."}
            </div>
          </div>
        )}
        {!loading && reqs.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                <Th>Requisition</Th>
                <Th>Department</Th>
                <Th>Location</Th>
                <Th>Headcount</Th>
                <Th>Urgency</Th>
                <Th>Status</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-rule hover:bg-paper transition-colors cursor-pointer"
                  onClick={() => (window.location.href = `/requisitions/${r.id}`)}
                  data-testid={`requisition-row-${r.id}`}
                >
                  <Td>
                    <div className="text-ink font-medium">{r.title}</div>
                    <div className="text-xs text-quiet font-mono mt-1">{r.requisition_code || "—"}</div>
                  </Td>
                  <Td>{r.department}</Td>
                  <Td>{r.location}</Td>
                  <Td className="font-mono">{r.headcount}</Td>
                  <Td>
                    <span className="font-mono text-xs uppercase tracking-wider">{r.urgency}</span>
                  </Td>
                  <Td>
                    <StatusBadge status={r.status} />
                  </Td>
                  <Td className="font-mono text-xs text-quiet">
                    {new Date(r.created_at).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {shareOpen && <ShareLinks onClose={() => setShareOpen(false)} />}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-quiet">
    {children}
  </th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-4 ${className}`}>{children}</td>;
